import React, { useState, useEffect, useRef, useContext } from 'react'
import { Table, Button, Popconfirm, notification, Input, Space } from 'antd'
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  SearchOutlined,
} from '@ant-design/icons'
import DepartmentModal from './DepartmentModal'
import { useQuery, useMutation } from '@apollo/react-hooks'
import { GET_DEPARTMENTS } from '../graphql/Queries'
import {
  CREATE_DEPARTMENT,
  UPDATE_DEPARTMENT,
  DELETE_DEPARTMENT,
} from '../graphql/Mutations'
import { AppContext } from 'AppContext'
import { canUserEdit, canUserAdd, canUserDelete, canUserSave } from 'utils/User'

const PAGE_SIZE = 10

const DepartmentTable = () => {
 
  const { state: { currentUser, userPermissions, isClient } = {} } = useContext(AppContext) || {}
  const canEdit = canUserEdit(userPermissions || currentUser, isClient)
  const canAdd = canUserAdd(userPermissions || currentUser, isClient)
  const canDelete = canUserDelete(userPermissions || currentUser, isClient)
  const canSave = canUserSave(userPermissions || currentUser, isClient)

  const { data, loading, refetch } = useQuery(GET_DEPARTMENTS, {
    fetchPolicy: 'network-only',
  })
  const [createDepartment] = useMutation(CREATE_DEPARTMENT)
  const [updateDepartment] = useMutation(UPDATE_DEPARTMENT)
  const [deleteDepartment] = useMutation(DELETE_DEPARTMENT)

  const [modalVisible, setModalVisible] = useState(false)
  const [editingDepartment, setEditingDepartment] = useState(null)
  const [currentPage, setCurrentPage] = useState(1)
  const [departments, setDepartments] = useState([])
  const searchInput = useRef(null)

  useEffect(() => {
    if (data?.getDepartments) {
      setDepartments(data.getDepartments)
    }
  }, [data])

  const handleAdd = () => {
    if (!canAdd) return
    setEditingDepartment(null)
    setModalVisible(true)
  }

  const handleEdit = (record) => {
    if (!canEdit) return
    setEditingDepartment(record)
    setModalVisible(true)
  }

  const handleDelete = async (id) => {
    if (!canDelete) return
    try {
      const res = await deleteDepartment({ variables: { id } })
      if (res?.data?.deleteDepartment?.success) {
        notification.success({ message: 'Department deleted' })
        refetch()
      } else {
        notification.info({ message: res?.data?.deleteDepartment?.message })
      }
    } catch (err) {
      notification.error({ message: 'Delete failed' })
    }
  }

  const handleModalOk = async (values) => {
    if (editingDepartment && (!canEdit || !canSave)) return
    if (!editingDepartment && (!canAdd || !canSave)) return
    try {
      if (editingDepartment) {
        await updateDepartment({
          variables: { id: editingDepartment.id, data: values },
        })
        notification.success({ message: 'Department updated' })
      } else {
        await createDepartment({ variables: { data: values } })
        notification.success({ message: 'Department created' })
      }
      setModalVisible(false)
      refetch()
    } catch (err) {
      notification.error({ message: 'Operation failed' })
    }
  }

  const getColumnSearchProps = (dataIndex, customRender) => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
    }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`Search ${dataIndex}`}
          value={selectedKeys[0]}
          onChange={(e) =>
            setSelectedKeys(e.target.value ? [e.target.value] : [])
          }
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type='primary'
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size='small'
            style={{ width: 90 }}
          >
            Search
          </Button>
          <Button
            onClick={() => handleReset(clearFilters)}
            size='small'
            style={{ width: 90 }}
          >
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: (filtered) => (
      <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />
    ),
    onFilter: (value, record) => {
      const fieldValue = customRender
        ? customRender(record)?.toString().toLowerCase()
        : record[dataIndex]?.toString().toLowerCase()
      return fieldValue?.includes(value.toLowerCase())
    },
    render: (text, record) => (customRender ? customRender(record) : text),
  })

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm()
    setCurrentPage(1)
  }

  const handleReset = (clearFilters) => {
    clearFilters()
    setCurrentPage(1)
  }

  const columns = [
    {
      title: 'Department',
      dataIndex: 'departmentName',
      key: 'departmentName',
      ...getColumnSearchProps('departmentName'),
    },
    {
      title: 'Department Head',
      key: 'headName',
      render: (_, record) =>
        `${record?.head?.firstName || ''} ${record?.head?.lastName || ''}`,
      ...getColumnSearchProps(
        'headName',
        (record) =>
          `${record?.head?.firstName || ''} ${record?.head?.lastName || ''}`,
      ),
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <>
          {canEdit && (
            <EditOutlined
              style={{ marginRight: 12, color: '#1890ff', cursor: 'pointer' }}
              onClick={() => handleEdit(record)}
            />
          )}
          {canDelete && (
            <Popconfirm title='Delete?' onConfirm={() => handleDelete(record.id)}>
              <DeleteOutlined style={{ color: 'red', cursor: 'pointer' }} />
            </Popconfirm>
          )}
        </>
      ),
    },
  ]

  return (
    <div>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div style={{ fontWeight: 600, fontSize: 18 }}>Department settings</div>
        {canAdd && (
          <Button type='primary' icon={<PlusOutlined />} onClick={handleAdd}>
            Add Department
          </Button>
        )}
      </div>
      <Table
        columns={columns}
        dataSource={departments}
        rowKey='id'
        loading={loading}
        pagination={{
          current: currentPage,
          pageSize: PAGE_SIZE,
          onChange: setCurrentPage,
        }}
      />
      <DepartmentModal
        visible={modalVisible}
        onCancel={() => setModalVisible(false)}
        onOk={handleModalOk}
        editingDepartment={editingDepartment}
        departments={departments}
      />
    </div>
  )
}

export default DepartmentTable
